import React, { FunctionComponent, memo, useCallback } from "react";
import { Grid, Box, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { Singleselect, ListOptionBase } from "@shared/components";
import ExtractionAwareInput from "@investorRequest/components/ExtractionAwareInput";
import FieldTooltip from "@investorRequest/components/FieldTooltip";
import { FIELD_TITLES } from "@investorRequest/constants/fieldTooltips";
import InvestorRequestTextarea from "@investorRequest/components/InvestorRequestTextarea";
import InvestorRequestDatepicker from "@investorRequest/components/InvestorRequestDatepicker";
import FurtherCreditFields from "@investorRequest/components/FurtherCreditFields";
import { validationMessages } from "@investorRequest/constants/validationMessages";
import { fieldKeys } from "@investorRequest/constants/fieldKeys";
import { useDropdownOverflowTooltip } from "@shared/hooks/useDropdownOverflowTooltip";
import { getDropdownWithIconStyles } from "@shared/styles/dropdownWithIconStyles";
import { useC2DSErrors } from "@investorRequest/hooks/useC2DSFieldError";
import C2DSFieldErrorMessages from "@investorRequest/components/C2DSFieldErrorMessages";
import { Nullable } from "@shared/models/general";
import {
  isValidRoutingNumber,
  isValidExternalRoutingNumber,
  sanitizeAsciiName,
  sanitizeDigits,
  sanitizeAccountNumberInput,
  isOverLimit,
  maxLengthMessage,
} from "../utils/SpecificValidations";
import { toEffectiveDateString } from "../utils/effectiveDate";

const useStyles = makeStyles((theme) => getDropdownWithIconStyles(theme));

interface Props {
  values: {
    bankName: string;
    beneficiaryAccountName: string;
    bankCountryLabel?: string;
    bankAccountNumber: string;
    routingNumber: string;
    effectiveDate?: string;
    furtherCreditYesNo?: string;
    furtherCreditLine1?: string;
    furtherCreditLine2?: string;
    fundType?: string;
  };
  countriesOptions: ListOptionBase[];
  selectedCountry?: ListOptionBase;
  onSelectCountry(option: ListOptionBase): void;
  onChange(field: string, value: string): void;
  showErrors: boolean;
  inputXS: 12 | 6;
  disableBankCountry?: boolean;
}

const InvestorRequestAchFields: FunctionComponent<Props> = ({
  values,
  countriesOptions,
  selectedCountry,
  onSelectCountry,
  onChange,
  showErrors,
  inputXS,
  disableBankCountry = false,
}) => {
  const classes = useStyles();
  const { selectedValueRef, dropdownOpen, isToggleOverflowing, selectKey, handleSelect, handleToggleButtonClick } =
    useDropdownOverflowTooltip({
      testId: "bank-country-select",
      selectedLabel: selectedCountry?.label,
    });

  // LPC-10289: C2DS validation errors
  const { hasFieldError, clearFieldError } = useC2DSErrors();

  // LPC-10289: Cache the C2DS error state for routing number to ensure consistency
  const hasRoutingNumberC2DSError = hasFieldError("routingNumber");

  const handleDatepickerChange = useCallback(
    (value: Nullable<Date>) => onChange("effectiveDate", value ? toEffectiveDateString(value) : ""),
    [onChange]
  );

  return (
    <>
      <Grid item xs={inputXS}>
        <FieldTooltip title={FIELD_TITLES.BeneficiaryAccountName}>
          <ExtractionAwareInput
            fieldName={fieldKeys.BeneficiaryAccountName}
            dense={false}
            placeholder="*Beneficiary Account Name"
            variant="light"
            size="large"
            value={values.beneficiaryAccountName}
            onValueChange={(v) => onChange(fieldKeys.BeneficiaryAccountName, sanitizeAsciiName(v))}
            sanitizeOnPaste={(t) => sanitizeAsciiName(t)}
            error={
              (showErrors && !(values.beneficiaryAccountName || "").trim()) ||
              isOverLimit(values.beneficiaryAccountName, 140)
            }
          />
        </FieldTooltip>
        {showErrors && !(values.beneficiaryAccountName || "").trim() && (
          <Box mt={1} color="warning.main" fontSize={12}>
            {validationMessages.beneficiaryAccountName.required}
          </Box>
        )}
        {isOverLimit(values.beneficiaryAccountName, 140) && (
          <Box mt={1} color="warning.main" fontSize={12}>
            {maxLengthMessage("Account Name", 140)}
          </Box>
        )}
        {showErrors &&
          !!(values.beneficiaryAccountName || "").trim() &&
          values.beneficiaryAccountName.length <= 140 &&
          /[^A-Za-z0-9\s]/.test(values.beneficiaryAccountName) && (
            <Box mt={1} color="warning.main" fontSize={12}>
              {validationMessages.beneficiaryAccountName.invalid}
            </Box>
          )}
      </Grid>

      <Grid item xs={inputXS}>
        <FieldTooltip title={FIELD_TITLES.BankName}>
          <ExtractionAwareInput
            fieldName={fieldKeys.BankName}
            dense={false}
            placeholder="*Beneficiary Bank Name"
            variant="light"
            size="large"
            value={values.bankName}
            onValueChange={(v) => onChange(fieldKeys.BankName, sanitizeAsciiName(v))}
            sanitizeOnPaste={(t) => sanitizeAsciiName(t)}
            error={(showErrors && !(values.bankName || "").trim()) || isOverLimit(values.bankName, 30)}
          />
        </FieldTooltip>
        {showErrors && !(values.bankName || "").trim() && (
          <Box mt={1} color="warning.main" fontSize={12}>
            {validationMessages.bankName.required}
          </Box>
        )}
        {isOverLimit(values.bankName, 30) && (
          <Box mt={1} color="warning.main" fontSize={12}>
            {maxLengthMessage("Bank Name", 30)}
          </Box>
        )}
        {showErrors &&
          !!(values.bankName || "").trim() &&
          values.bankName.length <= 30 &&
          /[^A-Za-z0-9\s]/.test(values.bankName) && (
            <Box mt={1} color="warning.main" fontSize={12}>
              {validationMessages.bankName.invalid}
            </Box>
          )}
      </Grid>
      <Grid item xs={inputXS}>
        <FieldTooltip title={FIELD_TITLES.AbaRoutingNumber}>
          <ExtractionAwareInput
            fieldName="routingNumber"
            dense={false}
            placeholder="*ABA / Routing #"
            variant="light"
            size="large"
            value={values.routingNumber}
            onValueChange={(v) => {
              // LPC-10289: Clear C2DS error when user modifies field
              clearFieldError("routingNumber");
              if (values.fundType === "External") {
                onChange("routingNumber", v.replace(/[^A-Za-z0-9]/g, ""));
              } else {
                onChange("routingNumber", sanitizeDigits(v));
              }
            }}
            sanitizeOnPaste={(t) =>
              values.fundType === "External" ? t.replace(/[^A-Za-z0-9]/g, "") : sanitizeDigits(t)
            }
            error={
              (showErrors &&
                (!values.routingNumber ||
                  (values.fundType === "External"
                    ? !isValidExternalRoutingNumber(values.routingNumber)
                    : !isValidRoutingNumber(values.routingNumber)))) ||
              isOverLimit(values.routingNumber, values.fundType === "External" ? 15 : 9) ||
              hasRoutingNumberC2DSError
            }
          />
        </FieldTooltip>
        {isOverLimit(values.routingNumber, values.fundType === "External" ? 15 : 9) ? (
          <Box mt={1} color="warning.main" fontSize={12}>
            {maxLengthMessage("Routing number", values.fundType === "External" ? 15 : 9)}
          </Box>
        ) : showErrors && !values.routingNumber ? (
          <Box mt={1} color="warning.main" fontSize={12}>
            {validationMessages.routingNumber.required}
          </Box>
        ) : showErrors && values.fundType === "External" ? (
          !isValidExternalRoutingNumber(values.routingNumber) ? (
            <Box mt={1} color="warning.main" fontSize={12}>
              Must be 9 digits or alphanumeric (max 15 characters)
            </Box>
          ) : null
        ) : showErrors && !isValidRoutingNumber(values.routingNumber) ? (
          <Box mt={1} color="warning.main" fontSize={12}>
            {validationMessages.routingNumber.invalid}
          </Box>
        ) : null}
        {/* LPC-10289: C2DS validation error */}
        <C2DSFieldErrorMessages fieldKey="routingNumber" />
      </Grid>
      <Grid item xs={inputXS}>
        <FieldTooltip title={FIELD_TITLES.BeneficiaryBankAccountNumber}>
          <ExtractionAwareInput
            fieldName={fieldKeys.BankAccountNumber}
            dense={false}
            placeholder="*Beneficiary Bank Account #"
            variant="light"
            size="large"
            value={values.bankAccountNumber}
            onValueChange={(v) => onChange(fieldKeys.BankAccountNumber, sanitizeAccountNumberInput(v, undefined, 10))}
            sanitizeOnPaste={(t) => sanitizeAccountNumberInput(t, undefined, 10)}
            error={(showErrors && !values.bankAccountNumber) || isOverLimit(values.bankAccountNumber, 35)}
          />
        </FieldTooltip>
        {showErrors && !values.bankAccountNumber && (
          <Box mt={1} color="warning.main" fontSize={12}>
            {validationMessages.bankAccountNumber.required}
          </Box>
        )}
        {isOverLimit(values.bankAccountNumber, 35) && (
          <Box mt={1} color="warning.main" fontSize={12}>
            {maxLengthMessage("Bank Account Number", 35)}
          </Box>
        )}
      </Grid>

      <Grid item xs={inputXS}>
        <FieldTooltip
          titleNode={
            <Box>
              <Typography>Beneficiary Bank Country</Typography>
              {isToggleOverflowing && selectedCountry?.label ? <Typography>{selectedCountry.label}</Typography> : null}
            </Box>
          }
          disableHover={dropdownOpen}
        >
          <Box className={classes.bankCountryContainer}>
            <Box className={classes.bankCountryDropdown} ref={selectedValueRef}>
              <Box>
                <Singleselect
                  key={selectKey}
                  options={countriesOptions}
                  selectedOption={selectedCountry}
                  onSelect={(option) => {
                    onSelectCountry(option);
                    handleSelect();
                  }}
                  onToggleButtonClick={handleToggleButtonClick}
                  label={selectedCountry?.label || "*Bank Country"}
                  variant="contained"
                  size="large"
                  withSearch
                  testId="bank-country-select"
                  disabled={disableBankCountry}
                />
              </Box>
            </Box>
          </Box>
        </FieldTooltip>
        {showErrors && !selectedCountry && (
          <Box mt={1} color="warning.main" fontSize={12}>
            {validationMessages.bankCountry.required}
          </Box>
        )}
      </Grid>

      <FurtherCreditFields
        yesNoValue={values.furtherCreditYesNo || ""}
        line1={values.furtherCreditLine1 || ""}
        line2={values.furtherCreditLine2 || ""}
        onChangeYesNo={(v) => {
          onChange("furtherCreditYesNo", v);
          // IR-UI-Revamp_V1: Clear further credit fields when "No" is selected
          if (v === "no") {
            onChange("furtherCreditLine1", "");
            onChange("furtherCreditLine2", "");
          }
        }}
        onChangeLine1={(v) => onChange("furtherCreditLine1", v)}
        onChangeLine2={(v) => onChange("furtherCreditLine2", v)}
        showErrors={showErrors}
      />

      <Grid item xs={inputXS} data-testid="investor-request-datepicker">
        <FieldTooltip title={FIELD_TITLES.DateInstructionsBecomeEffective}>
          <InvestorRequestDatepicker value={values.effectiveDate || ""} onChange={handleDatepickerChange} />
        </FieldTooltip>
      </Grid>

      <Grid item xs={12}>
        <FieldTooltip title={FIELD_TITLES.AdditionalNote}>
          <InvestorRequestTextarea
            placeholder="Please include any additional notes"
            value={(values as any).freeText || ""}
            onChange={(v: string) => onChange("freeText", v)}
            error={isOverLimit((values as any).freeText, 8000)}
          />
        </FieldTooltip>
        {isOverLimit((values as any).freeText, 8000) && (
          <Box mt={1} color="warning.main" fontSize={12}>
            {maxLengthMessage("Additional Notes", 8000)}
          </Box>
        )}
      </Grid>
    </>
  );
};

export default memo(InvestorRequestAchFields);
